import React, { useEffect } from 'react';
import { JiraSoftwareBoardAri } from '@atlassian/ari/jira-software/board';
import { useBoardId } from '@atlassian/jira-board-settings-common/src/common/utils/use-board-id';
import { useEntryPoint } from '@atlassian/jira-entry-point';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container';
import { useResource } from '@atlassian/jira-router';
import { editModelResource } from '@atlassian/jira-router-resources-classic-projects';
import { BoardSettingsGeneralSettingsSkeleton } from '@atlassian/jira-skeletons';
import type { RapidBoardConfig } from '@atlassian/jira-software-board-settings-rapidboard-config-common';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { APP_SOURCE_NAME, APP_TEAM_NAME } from './common/constants';
import { generalPageEntryPoint } from './entrypoint';

export const GeneralSettingsPage = () => {
	const { data } = useResource(editModelResource);

	if (!data) {
		return null;
	}

	return <GeneralSettingsContent {...data} />;
};

const GeneralSettingsContent = (props: RapidBoardConfig) => {
	const boardId = useBoardId();
	const cloudId = useCloudId();

	const boardAri = JiraSoftwareBoardAri.create({
		siteId: cloudId,
		boardId: String(boardId),
	}).toString();

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(generalPageEntryPoint, {
		boardId: boardAri,
	});

	useEffect(() => {
		entryPointActions.load();
		return () => {
			entryPointActions.unload();
		};
	}, [entryPointActions]);

	return (
		<JiraEntryPointContainer
			id="jira-general-setting-page"
			packageName={APP_SOURCE_NAME}
			teamName={APP_TEAM_NAME}
			entryPointReferenceSubject={entryPointReferenceSubject}
			runtimeProps={{
				rapidViewId: props.id,
				warnBeforeEditingOwner: props.warnBeforeEditingOwner,
				warnBeforeEditingLocation: props.warnBeforeEditingLocation,
				name: props.name,
				canEdit: props.canEdit,
				boardLocationModel: props.boardLocationModel,
				filterConfig: props.filterConfig,
				oldDoneIssuesCutoff: props.oldDoneIssuesCutoff,
				oldDoneIssuesCutoffOptions: props.oldDoneIssuesCutoffOptions,
				boardAdmins: props.boardAdmins,
				canUseBoardAdminsPicker: props.canUseBoardAdminsPicker,
				subqueryConfig: props.subqueryConfig,
				isSprintSupportEnabled: props.isSprintSupportEnabled,
				isCMPBoard: !props.isSimplifiedProject,
				userConfig: props.globalConfig.userConfig,
			}}
			fallback={<BoardSettingsGeneralSettingsSkeleton />}
		/>
	);
};

export default GeneralSettingsPage;
